import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { z } from 'zod'
import { ReservedResourceF } from '../../../types/reservation-detail.types'
import useReservedResourcesBulkEditCRUD from './reserved-resources-bulk-edit-crud'
import { OvbResolveType } from 'src/api/api.generated'

export type RRBulkEditFormData = {
  pricePerNight?: number
  rateId?: string
  chargeCategoryId?: string
  checkIn?: Date
  checkOut?: Date
  adults?: number
  children?: { childId: string; quantity: number }[]
}

const useRRBulkEditForm = (propertyId: string, reservedResources: ReservedResourceF[], maxAdults: number, maxOccupancy: number, onRRUpdated?: () => void) => {
  const { t } = useTranslation()
  const rrBulkEditForm = z
    .object({
      pricePerNight: z.number().min(0).optional(),
      rateId: z.string().optional(),
      chargeCategoryId: z.string().optional(),
      checkIn: z.date().optional(),
      checkOut: z.date().optional(),
      adults: z.number().min(0).max(maxAdults).optional(),
      children: z
        .array(
          z.object({
            childId: z.string(),
            quantity: z.number(),
            noOccupancy: z.boolean(),
          }),
        )
        .optional(),
    })
    .refine((data) => !data.checkOut || (data.checkIn && data.checkOut), {
      message: t('validation.required_error'),
      path: ['checkIn'],
    })
    .refine((data) => !data.checkIn || (data.checkIn && data.checkOut), {
      message: t('validation.required_error'),
      path: ['checkOut'],
    })
    .refine((data) => !data.rateId || (data.rateId && data.chargeCategoryId), {
      message: t('validation.required_error'),
      path: ['chargeCategoryId'],
    })
    .refine(
      (data) => {
        const tmpAdults = data.adults ?? 0
        const tmpChildren = data.children?.reduce((prev, child) => prev + child.quantity, 0) ?? 0
        return tmpAdults + tmpChildren <= maxOccupancy
      },
      {
        message: t('pms.booking.reservation.rr_bulk_edit_dialog.form.occupation_overflow', { maxOccupancy: maxOccupancy }),
        path: ['adults', 'children'],
      },
    )

  type RRBulkEditForm = z.infer<typeof rrBulkEditForm>

  const { updateRRs, loading: loadingSubmit } = useReservedResourcesBulkEditCRUD(propertyId, onRRUpdated)

  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RRBulkEditForm>({ resolver: zodResolver(rrBulkEditForm) })

  useEffect(() => {
    console.log('errors')
    console.log(errors)
  }, [errors])

  const submitHandler: SubmitHandler<RRBulkEditForm> = useCallback(
    async (data: RRBulkEditFormData) => {
      return await updateRRs(reservedResources, data, OvbResolveType.ERROR)
    },
    [updateRRs],
  )

  const onFormSubmit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  return { rrBulkEditForm, onFormSubmit, control, errors, setValue, watch, loadingSubmit }
}

export default useRRBulkEditForm
